import React from 'react';
import {IconButton, Form, Input} from 'rsuite';
import MinusIcon from '@rsuite/icons/Minus';
import PlusIcon from '@rsuite/icons/Plus';
import TrashIcon from '@rsuite/icons/Trash';
import {useCartQuery} from '@/api/useCartQuery';
import {useSessionInfoQuery} from '@/api/useSessionInfoQuery';
import {useCurrenciesStore} from '@/state/currenciesStore';
import {formatCurrency, convertCurrencyAmount} from '@/utils/currencyUtils';
import type {LineItem} from '@/types';
import {cn} from '@bem-react/classname';
import styles from './CartItem.module.scss';

export type CartItemProps = {
  item: LineItem;
  onChangeQty: (qty: number) => void;
  onRemove: () => void;
  isUpdating?: boolean;
  isRemoving?: boolean;
};

export default function CartItem({item, onChangeQty, onRemove, isUpdating, isRemoving}: CartItemProps) {
  // Initialize BEM block with plain string; map to CSS module via styles[cnBlock(...)]
  const cnCart = cn('cCartItem');

  // Selected currency ISO and conversion rates
  const {data: cart} = useCartQuery();
  const { data: session } = useSessionInfoQuery();
  const currency = cart?.paymentCurrency;
  const rates = useCurrenciesStore((s) => s.rates);

  // Prefer numeric totals from line item; fallback order: total -> price -> subtotal
  const baseIso = 'NOK';
  const rawAmount = (typeof item.total === 'number' ? item.total : undefined)
    ?? (typeof item.price === 'number' ? item.price : undefined)
    ?? (typeof item.subtotal === 'number' ? item.subtotal : 0);

  // Determine if PPD pricing is enabled for the user (tolerate different casing)
  const usePpd = (session as any)?.usePpd || (session as any)?.usePPD || false;

  // Sum tax adjustments on the line item
  const taxAdjustmentsTotal = (item.adjustments || [])
    .filter((adj) => String(adj?.type).toLowerCase() === 'tax')
    .reduce((sum, adj) => sum + (Number(adj?.amount) || 0), 0);

  // If PPD is enabled, show price without VAT: subtract tax from the amount
  const amountForDisplay = usePpd ? (rawAmount - taxAdjustmentsTotal) : rawAmount;

  const convertedAmount = convertCurrencyAmount(amountForDisplay, rates, baseIso, currency);

  const qty = item.qty || 1;
  return (
    <div className={cnCart()}> {/* root block */}
      <div className={cnCart('Info')}> {/* left info column */}
        <div className={cnCart('Artist')}> {item.snapshot?.product?.productData?.productArtistPreparse}</div>

        <div className={cnCart('Title')}>
          {item.snapshot?.product?.title || item.snapshot?.title || item.description || item.sku}

          <span className={cnCart('Variant')}>
          {item.snapshot?.title}
        </span>
        </div>

      </div>
      <div className={cnCart('Actions')}> {/* right actions */}
        <IconButton
          size="xs"
          appearance="default"
          circle
          icon={<MinusIcon/>}
          aria-label="Decrease quantity"
          onClick={() => onChangeQty(Math.max(0, qty - 1))}
          disabled={!!isUpdating}
        />
        <Form className={styles[cnCart('QtyForm')]} fluid>
          <Form.Control
            name="qty"
            accepter={Input}
            type="number"
            value={qty}
            min={0}
            step={1}
            onChange={(val) => onChangeQty(Math.max(0, Number(val) || 0))}
            disabled={!!isUpdating}
            style={{width: '2.5em'}}
          />
        </Form>
        <IconButton
          size="xs"
          appearance="default"
          circle
          icon={<PlusIcon/>}
          aria-label="Increase quantity"
          onClick={() => onChangeQty(qty + 1)}
          disabled={!!isUpdating}
        />
        <div className={cnCart('Price')}>
          {formatCurrency(convertedAmount, currency)}
        </div>
        <IconButton size="xs" appearance="primary" circle onClick={onRemove} disabled={!!isRemoving}
                    icon={<TrashIcon/>}/>
      </div>
    </div>
  );
}
